import React, { useState, useEffect } from "react";
import { Phone, Mail, Clock, CheckCircle2, Loader2, Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { createBooking, fetchSlots } from "@/lib/api";

const SERVICES = [
  "Seasonal Tire Changeover",
  "Tire Installation",
  "Tire Repair (Flat / Puncture)",
  "Tire Rotation",
  "Wheel Balancing",
  "Roadside Assistance",
  "TPMS Inspection",
  "Fleet Service",
  "Other",
];

const initial = {
  name: "", phone: "", email: "",
  vehicle_make: "", vehicle_model: "",
  service_needed: "", preferred_date: "", preferred_time: "", message: "",
};

// today (YYYY-MM-DD) in local tz
const todayISO = () => {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
};

const formatSlot = (t) => {
  const [h] = t.split(":").map(Number);
  const suffix = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:00 ${suffix}`;
};

export const BookingForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  // Load slots when date changes
  useEffect(() => {
    if (!form.preferred_date) { setSlots([]); return; }
    let cancelled = false;
    setSlotsLoading(true);
    fetchSlots(form.preferred_date)
      .then((d) => { if (!cancelled) setSlots(d.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    // reset time if not available anymore
    setForm((f) => ({ ...f, preferred_time: "" }));
    return () => { cancelled = true; };
  }, [form.preferred_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.vehicle_make || !form.vehicle_model || !form.service_needed || !form.preferred_date || !form.preferred_time) {
      toast.error("Please fill all required fields, including a time slot.");
      return;
    }
    setLoading(true);
    try {
      await createBooking(form);
      setDone(true);
      setForm(initial);
      setSlots([]);
      toast.success("Booking received! We'll be in touch shortly.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to submit booking";
      toast.error(typeof msg === "string" ? msg : "Failed to submit booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="section-mv bg-brand-surface">
      <div className="container-mv grid lg:grid-cols-12 gap-10 items-start">
        {/* Left: form */}
        <div className="lg:col-span-7 rounded-2xl bg-white border border-neutral-200 shadow-premium p-6 sm:p-10">
          <div className="text-xs font-semibold tracking-widest text-brand-red uppercase">Book Service</div>
          <h2 className="mt-2 font-display font-bold text-3xl sm:text-4xl tracking-tight text-brand-black">
            Book Your Mobile Tire Service Today
          </h2>
          <p className="mt-3 text-neutral-600">Pick a date, choose an open time slot — we'll confirm by phone.</p>

          {done ? (
            <div className="mt-8 rounded-xl border border-green-200 bg-green-50 p-8 text-center" data-testid="booking-success">
              <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
              <h3 className="mt-3 font-display font-bold text-xl text-brand-black">Booking received</h3>
              <p className="mt-2 text-neutral-600">Thanks — we've emailed the details to our team and will confirm your appointment shortly.</p>
              <button onClick={() => setDone(false)} className="mt-6 inline-flex items-center rounded-full bg-brand-black text-white px-5 py-2.5 text-sm font-semibold hover:bg-neutral-800 active:scale-95 transition-all" data-testid="booking-new">
                Submit another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="booking-form">
              <Field label="Full Name *"><Input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" /></Field>
              <Field label="Phone *"><Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder="403-000-0000" /></Field>
              <Field label="Email *" className="sm:col-span-2"><Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" /></Field>
              <Field label="Vehicle Make *"><Input data-testid="booking-make" value={form.vehicle_make} onChange={set("vehicle_make")} placeholder="Toyota" /></Field>
              <Field label="Vehicle Model *"><Input data-testid="booking-model" value={form.vehicle_model} onChange={set("vehicle_model")} placeholder="RAV4" /></Field>
              <Field label="Service Needed *" className="sm:col-span-2">
                <Select value={form.service_needed} onValueChange={(v) => setForm((f) => ({ ...f, service_needed: v }))}>
                  <SelectTrigger data-testid="booking-service" className="h-10"><SelectValue placeholder="Select a service" /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Preferred Date *" className="sm:col-span-2">
                <Input
                  data-testid="booking-date"
                  type="date"
                  min={todayISO()}
                  value={form.preferred_date}
                  onChange={set("preferred_date")}
                />
              </Field>

              {/* Time slots grid */}
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold text-neutral-700 flex items-center gap-2">
                  <CalendarIcon className="w-3.5 h-3.5 text-brand-red" /> Available Time Slots *
                </Label>
                <div className="mt-2 min-h-[76px]">
                  {!form.preferred_date ? (
                    <div className="text-sm text-neutral-500 rounded-lg border border-dashed border-neutral-300 p-4 bg-neutral-50">
                      Pick a date to see available time slots.
                    </div>
                  ) : slotsLoading ? (
                    <div className="text-sm text-neutral-500 inline-flex items-center gap-2 p-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading slots…</div>
                  ) : slots.length === 0 ? (
                    <div className="text-sm text-neutral-500 rounded-lg border border-dashed border-neutral-300 p-4 bg-neutral-50">
                      No slots available for this date.
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2" data-testid="slot-grid">
                      {slots.map((s) => {
                        const selected = form.preferred_time === s.time;
                        const base = "text-xs font-semibold rounded-lg border px-2 py-2.5 transition-all";
                        const cls = !s.available
                          ? `${base} bg-neutral-100 border-neutral-200 text-neutral-400 line-through cursor-not-allowed`
                          : selected
                            ? `${base} bg-brand-red border-brand-red text-white shadow`
                            : `${base} bg-white border-neutral-200 text-brand-black hover:border-brand-red hover:text-brand-red active:scale-95`;
                        return (
                          <button
                            type="button"
                            key={s.time}
                            disabled={!s.available}
                            onClick={() => setForm((f) => ({ ...f, preferred_time: s.time }))}
                            className={cls}
                            data-testid={`slot-${s.time}`}
                            aria-pressed={selected}
                          >
                            {formatSlot(s.time)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                {form.preferred_time && (
                  <p className="mt-2 text-xs text-neutral-600">Selected: <span className="font-semibold text-brand-black">{formatSlot(form.preferred_time)}</span></p>
                )}
              </div>

              <Field label="Additional Details" className="sm:col-span-2">
                <Textarea data-testid="booking-message" value={form.message} onChange={set("message")} placeholder="Anything else we should know?" rows={4} />
              </Field>

              <div className="sm:col-span-2 mt-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-red text-white px-7 py-3.5 text-sm font-semibold hover:bg-[#CC0000] active:scale-95 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Book Mobile Tire Service</>}
                </button>
                <p className="mt-3 text-xs text-neutral-500 text-center">By submitting you agree to be contacted by Mavtech regarding your appointment.</p>
              </div>
            </form>
          )}
        </div>

        {/* Right: contact info */}
        <div className="lg:col-span-5 space-y-6">
          <div className="rounded-2xl bg-brand-black text-white p-8 shadow-premium">
            <h3 className="font-display font-bold text-2xl">Talk to Mavtech</h3>
            <p className="mt-2 text-white/70 text-sm">Fastest response by phone. We answer 7 days a week.</p>
            <div className="mt-6 space-y-4">
              <a href="tel:4034043515" className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-lg bg-brand-red/15 text-brand-red flex items-center justify-center"><Phone className="w-4 h-4" /></span>
                <span>
                  <span className="block text-xs text-white/60">Phone</span>
                  <span className="block font-semibold group-hover:text-brand-red transition-colors">403-404-3515</span>
                </span>
              </a>
              <a href="mailto:mavtechtires@hotmail.com" className="flex items-center gap-3 group">
                <span className="w-10 h-10 rounded-lg bg-brand-red/15 text-brand-red flex items-center justify-center"><Mail className="w-4 h-4" /></span>
                <span>
                  <span className="block text-xs text-white/60">Email</span>
                  <span className="block font-semibold group-hover:text-brand-red transition-colors">mavtechtires@hotmail.com</span>
                </span>
              </a>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-lg bg-brand-red/15 text-brand-red flex items-center justify-center"><Clock className="w-4 h-4" /></span>
                <span>
                  <span className="block text-xs text-white/60">Hours</span>
                  <span className="block font-semibold">Mon–Sun · 7:00 AM – 8:00 PM</span>
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-2xl overflow-hidden border border-neutral-200 shadow-premium">
            <img
              src="https://images.unsplash.com/photo-1563161084-b4c9f34d60ae?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85"
              alt="Open road"
              className="w-full h-56 object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <Label className="text-xs font-semibold text-neutral-700">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default BookingForm;
